<!-- eslint-disable no-undef -->
<script setup>
    // Imports
    import { ref, watch, onMounted, computed, nextTick } from 'vue';
    import JsonEditor from '@/components/common/JsonEditor.vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import { Action } from '@/api/action';

    const props = defineProps({
        dataID: {
            type: String,
            required: true
        }
    });
    
    // References
    const selectedSetting = ref(null);
    const actionDetails = ref(null);
    const payload = ref({});
    const payloadValues = ref({});
    const editorVisible = ref(false);
    const loading = ref(false);
    const result = ref(null);
    const resultType = ref(null); // 'success' or 'error'
    const actions = ref({});
    const loadingActions = ref(true);

    // Lifecycle hooks
    onMounted(async() => {
        const response = await Action.get(`/options?version=1`);
        if (response.status === 'success') {
            actions.value = response.details;
        }
        loadingActions.value = false;
    });

    // Watches
    watch(actionDetails, async () => {
        result.value = null;
        resultType.value = null;
        editorVisible.value = false;
        
        // Use nextTick instead of setTimeout for proper Vue reactivity
        await nextTick();
        setPayload();
    });

    // Methods
    const sortedActions = computed(() => {
        return Object.entries(actions.value)
            .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''));
    });

    const filteredActions = computed(() => {
        if (!selectedSetting.value || !actions.value[selectedSetting.value]) return [];
        
        return Object.entries(actions.value[selectedSetting.value])
            .filter(([key]) => key !== 'name')
            .sort(([a], [b]) => a.localeCompare(b));
    });

    const hasActions = computed(() => {
        return Object.keys(actions.value).length > 0;
    });

    const getActionsFullList = (classObj) => {
        return Object.entries(classObj)
            .filter(([k]) => k !== 'name')
            .map(([k]) => k)
            .sort((a, b) => a.localeCompare(b));
    };

    const getActionsOverflowList = (classObj) => {
        return getActionsFullList(classObj).slice(3);
    };

    const setPayload = async () => {
        if (actionDetails.value && actionDetails.value.payload) {
            
            // Payload is a string, so use it as is
            if (typeof actionDetails.value.payload === 'string') {
                payloadValues.value = {};
                payload.value = actionDetails.value.payload;
                editorVisible.value = false;
            } 
            
            // Payload is a valid JSON object, so start the editor
            else {
                const actionKey = Object.keys(actionDetails.value.payload)[0];
                payloadValues.value = actionDetails.value.payload[actionKey];
                editorVisible.value = true;
            }
        } else {
            payload.value = {};
            editorVisible.value = false;
        }
    }

    const resetForm = () => {
        selectedSetting.value = null;
        actionDetails.value = null;
        payload.value = {};
        payloadValues.value = {};
        editorVisible.value = false;
    }

    const sendRequest = async () => {
        loading.value = true;
        result.value = null;
        resultType.value = null;

        if (Object.keys(payloadValues.value).length) {
            const actionKey = Object.keys(actionDetails.value.payload)[0];
            payload.value = {
                [actionKey]: JSON.stringify(payloadValues.value)
            };
        }

        let body = JSON.stringify({
            dataID: props.dataID,
            actionClass: selectedSetting.value,
            payload: payload.value
        });

        let response = null;
        if (actionDetails.value.method === "POST") {
            response = await Action.post('', body);
        }
        else if (actionDetails.value.method === "GET") {
            response = await Action.get(`?dataID=${props.dataID}&actionClass=${selectedSetting.value}&payload=${payload.value}`);
        }
        else if (actionDetails.value.method === "PUT") {
            response = await Action.put('', body);
        }
        else if (actionDetails.value.method === "DELETE") {
            response = await Action.delete('', body);
        }
        loading.value = false;

        if (response.status === 'success') {
            result.value = "Request sent";
        }
        else {
            result.value = "Request failed";
        }
    }
</script>

<template>
    <div class="flex flex-col gap-4">

        <!-- Loading -->
        <div v-if="loadingActions" class="flex flex-col items-center justify-center py-10 gap-3">
            <LoadingAnimation3 size="36px" />
            <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Loading available actions…</p>
        </div>

        <!-- No actions -->
        <div v-else-if="!hasActions" class="flex flex-col items-center justify-center py-10 text-center gap-2">
            <p class="font-body font-semibold text-sm text-mc-ink dark:text-on-dark">No actions available</p>
            <p class="font-body text-xs text-ink-muted dark:text-on-dark-muted">Please try again later</p>
        </div>

        <!-- Form -->
        <form v-else id="actions-request" class="flex flex-col gap-5" @submit.prevent="sendRequest">

            <!-- Step 1: Category cards -->
            <div>
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Action Category</div>
                <div class="grid grid-cols-2 gap-2">
                    <button
                        v-for="[classKey, classDetails] in sortedActions"
                        :key="classKey"
                        type="button"
                        :disabled="loading"
                        class="relative text-left px-3 py-2.5 rounded-mc-md border transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                        :class="selectedSetting === classKey
                            ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                            : ['border-mc-steel dark:border-line-on-dark bg-transparent hover:border-mc-ink dark:hover:border-on-dark-muted', getActionsOverflowList(classDetails).length ? 'group hover:rounded-b-none hover:z-10' : '']"
                        @click="selectedSetting = selectedSetting === classKey ? null : classKey; actionDetails = null"
                    >
                        <div class="font-display font-bold text-sm text-mc-ink dark:text-on-dark">{{ classDetails.name || classKey }}</div>
                        <div class="font-mono text-xs text-ink-muted dark:text-on-dark-muted mt-1 truncate">
                            <template v-for="(name, i) in getActionsFullList(classDetails).slice(0, 3)" :key="name">{{ i > 0 ? ' · ' : '' }}{{ name }}</template><span v-if="getActionsOverflowList(classDetails).length" class="transition-opacity duration-200 ease-out group-hover:opacity-0"> · More...</span>
                        </div>
                        <!-- Hover overlay: overflow items only, card text stays in place -->
                        <div
                            v-if="getActionsOverflowList(classDetails).length"
                            class="absolute top-full left-[-1px] right-[-1px] border border-t-0 rounded-b-mc-md px-3 pb-2.5 overflow-hidden max-h-0 opacity-0 group-hover:max-h-[20rem] group-hover:opacity-100 group-hover:border-mc-ink dark:group-hover:border-on-dark-muted transition-[max-height,opacity,border-color] duration-200 ease-in-out pointer-events-none z-20"
                            :class="selectedSetting === classKey
                                ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                                : 'border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-mc-charcoal'"
                        >
                            <template v-for="(name, i) in getActionsOverflowList(classDetails)" :key="name">
                                <span class="inline-block whitespace-nowrap font-mono text-xs text-ink-muted dark:text-on-dark-muted">{{ i === 0 ? ' · ' : '' }}{{ name }}</span>{{ i < getActionsOverflowList(classDetails).length - 1 ? ' · ' : '' }}
                            </template>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Step 2: Action rows (after category selected) -->
            <div v-if="selectedSetting">
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Action</div>
                <div class="flex flex-col gap-1.5">
                    <button
                        v-for="[actionKey, details] in filteredActions"
                        :key="actionKey"
                        type="button"
                        :disabled="loading"
                        class="flex items-center justify-between px-3 py-2.5 rounded-mc-sm border transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                        :class="actionDetails === details
                            ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                            : 'border-mc-steel dark:border-line-on-dark hover:border-mc-ink dark:hover:border-on-dark-muted bg-transparent'"
                        @click="actionDetails = details; setPayload()"
                    >
                        <span class="font-body text-sm text-mc-ink dark:text-on-dark">{{ actionKey }}</span>
                        <span
                            v-if="details.method"
                            class="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ml-2 shrink-0"
                            :class="{
                                'bg-green-500/15 text-green-700 dark:text-green-400': details.method === 'POST',
                                'bg-mc-blue/15 text-mc-blue': details.method === 'GET',
                                'bg-amber-500/15 text-amber-700 dark:text-amber-400': details.method === 'PUT',
                                'bg-mc-red/15 text-mc-red': details.method === 'DELETE',
                            }"
                        >{{ details.method }}</span>
                    </button>
                </div>
            </div>

            <!-- Payload editor -->
            <transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 -translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-1"
            >
                <div v-if="editorVisible" class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-3">
                    <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Payload</div>
                    <JsonEditor
                        v-model:json-value="payloadValues"
                        title="Payload"
                        :advanced="true"
                    />
                </div>
            </transition>

            <!-- Send row -->
            <div class="flex items-center gap-3 pt-1 border-t border-mc-steel/50 dark:border-line-on-dark">
                <button
                    type="submit"
                    class="btn-primary"
                    :disabled="!actionDetails || loading"
                >
                    {{ loading ? 'Sending…' : 'Send Request' }}
                </button>

                <button
                    v-if="selectedSetting"
                    type="button"
                    class="btn-secondary"
                    :disabled="loading"
                    @click="resetForm"
                >
                    Reset
                </button>

                <LoadingAnimation3 v-if="loading" size="24px" />

                <transition
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                >
                    <span
                        v-if="result"
                        class="font-body text-sm font-semibold"
                        :class="resultType === 'success' ? 'text-green-600 dark:text-green-400' : 'text-mc-red'"
                    >
                        {{ result }}
                    </span>
                </transition>
            </div>

        </form>
    </div>
</template>
